import { LOCALE_ID, StaticProvider } from '@angular/core';
import { loadTranslations } from '@angular/localize';
import { bootstrapApplication } from '@angular/platform-browser';

import {
  MUL_COOKIE_ID,
  getBrowserLanguage,
  resolvePreferredLangCookie,
} from '@mimecast-ui/shared/i18n-utils';
import {
  getFaroRuntimeConfig,
  initFaro,
} from '@mimecast-ui/shared/observability';

import { App } from './app/app';
import { appConfig } from './app/app.config';
import { shouldLoadTranslations } from './bootstrap-language.utils';

// GTAs that ship translated login pages - all others bootstrap in English.
// To add a new GTA, append its value here and redeploy (no Java change needed).
const TRANSLATED_GTAS = ['portal', 'secure'] as const;

if (typeof IS_LOCAL_ENV === 'undefined' || !IS_LOCAL_ENV) {
  try {
    // Uses 'mimecast.grid' to match the key the existing fep-mul-login Grafana
    // dashboard was built against. Other products use the default 'region'.
    const faroRuntime = getFaroRuntimeConfig(
      'universal-login',
      window.location.hostname,
      { gridAttributeKey: 'mimecast.grid' }
    );
    initFaro({
      appName: faroRuntime.appName,
      collectorUrl: faroRuntime.collectorUrl,
      enabled: faroRuntime.enabled,
      environment: faroRuntime.faroEnvironment,
      version: JSON.stringify(process.env['mimecast_ui_package_version']),
      resourceAttributes: faroRuntime.resourceAttributes,
      // ru carries an opaque emailToken in the URL for discovery handoff/redirects -
      // it must never leak into telemetry.
      excludeUrlParams: ['ru'],
    });
  } catch (_) {
    console.warn('Error initializing Faro');
  }
}

function init(providers?: StaticProvider[]): void {
  const config = providers?.length
    ? { ...appConfig, providers: [...appConfig.providers, ...providers] }
    : appConfig;

  bootstrapApplication(App, config).catch((err) => console.error(err));
}

const gta = new URLSearchParams(window.location.search).get('gta') ?? '';

const rawCookie = document.cookie
  .split('; ')
  .find((row) => row.startsWith(`${MUL_COOKIE_ID}=`))
  ?.split('=')[1];

const initialLanguage: string = rawCookie
  ? (resolvePreferredLangCookie(rawCookie) ?? 'en-US')
  : getBrowserLanguage();

const needsTranslations = shouldLoadTranslations(
  gta,
  initialLanguage,
  TRANSLATED_GTAS
);

if (needsTranslations) {
  const importUrlMsgPath = `${initialLanguage}/messages.${initialLanguage}`;

  import(`./locale/${importUrlMsgPath}.json`)
    .then((data) => {
      if (data.locale && data.translations) {
        loadTranslations(data.translations);
        init([{ provide: LOCALE_ID, useValue: data.locale }]);
      } else {
        console.warn('[Bootstrap] Invalid translation format, using default');
        init();
      }
    })
    .catch((err) => {
      console.warn(
        `[Bootstrap] Failed to load translations for ${initialLanguage}, using default`,
        err
      );
      init();
    });
} else {
  init();
}
